import moment from "moment";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { CONTRACT_SEARCH_MAX_LENGTH } from "@shyne/shared/api";
import { useDailyWorkForVendor } from "./daily-work-vendor.hooks";
import { DailyWorkJoin } from "@/repositories/daily-work.repository";
import { Button } from "@/components/ui/button";
import { Plus, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { daily_work_status } from "@/models/daily-work.model";
import CreateDailyWorkModal from "./components/CreateDailyWorkModal";
import UpdateDailyWorkModal from "./components/UpdateDailyWorkModal";
import BulkUploadModal from "./components/BulkUploadModal";

export default function DailyWorkPage() {
  const { list, total, filters, pagination, loading, refetch } = useDailyWorkForVendor();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [selectedDailyWork, setSelectedDailyWork] = useState<DailyWorkJoin | null>(null);

  const filterItems: FilterItem[] = useMemo(
    () => [
      {
        type: "search",
        title: "Search Property",
        value: filters.search,
        onValueChange: filters.setSearch,
        maxLength: CONTRACT_SEARCH_MAX_LENGTH,
        grid: 3,
      },
      {
        type: "select",
        title: "Status",
        value: filters.status,
        onValueChange: (value) => filters.setStatus(value),
        options: [
          { value: daily_work_status.active, label: "Active" },
          { value: daily_work_status.paused, label: "Paused" },
          { value: daily_work_status.cancelled, label: "Cancelled" },
        ],
        grid: 2,
      },
    ],
    [
      filters.search,
      filters.status,
    ],
  );

  const getStatusBadge = (status: daily_work_status) => {
    const variants: Record<daily_work_status, "default" | "secondary" | "destructive"> = {
      [daily_work_status.active]: "default",
      [daily_work_status.paused]: "secondary",
      [daily_work_status.cancelled]: "destructive",
    };
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  const cols = useMemo<ColumnDef<DailyWorkJoin>[]>(
    () => [
      { header: "ID", accessor: (r) => r.id.slice(0, 8) },
      {
        header: "Property",
        accessor: (r) => (
          <Link to={`/dailywork/${r.id}`} className="flex flex-col hover:underline">
            <span className="font-medium">{r.property.name}</span>
            {r.property.address && <span className="text-sm text-muted-foreground">{r.property.address}</span>}
          </Link>
        ),
        sortable: true,
        sortKey: "properties.name",
        cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
      },
      {
        header: "Status",
        accessor: (r) => getStatusBadge(r.status),
      },
      {
        header: "Start Date",
        accessor: (r) => (r.start_date ? moment(r.start_date).format("MM/DD/YYYY") : "-"),
        sortable: true,
        sortKey: "start_date",
      },
      {
        header: "End Date",
        accessor: (r) => (r.end_date ? moment(r.end_date).format("MM/DD/YYYY") : "Ongoing"),
        sortable: true,
        sortKey: "end_date",
      },
      {
        header: "Monthly Price",
        accessor: (r) =>
          r.monthly_price === null || r.monthly_price === undefined ? (
            <span className="text-muted-foreground">Not billed</span>
          ) : (
            `$${r.monthly_price.toFixed(2)}`
          ),
        sortable: true,
        sortKey: "monthly_price",
      },
      {
        header: "Created",
        accessor: (r) => moment(r.created_at).format("MM/DD/YYYY"),
        sortable: true,
        sortKey: "created_at",
      },
      {
        header: "Actions",
        accessor: (row) => (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedDailyWork(row);
                setUpdateModalOpen(true);
              }}
            >
              Edit
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/dailywork/${row.id}`}>View</Link>
            </Button>
          </div>
        ),
        className: "whitespace-nowrap",
      },
    ],
    [],
  );

  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    refetch();
  };

  const handleUpdateSuccess = () => {
    setUpdateModalOpen(false);
    setSelectedDailyWork(null);
    refetch();
  };

  return (
    <MainLayout
      breadcrumb={[{ name: "Daily Work", href: "#" }]}
      pageHeader={{
        title: "Property Contracts",
        subtitle: "Manage the Daily Work contracts that recurring schedules are built under",
        buttonText: "Create Property Contract",
        onButtonClick: () => setCreateModalOpen(true),
        additionalButtons: (
          <Button
            variant="outline"
            size="lg"
            onClick={() => setBulkUploadModalOpen(true)}
            className="uppercase rounded-lg"
          >
            <Upload className="size-4 mr-2" />
            Bulk upload
          </Button>
        ),
      }}
    >
      <SimpleTable<DailyWorkJoin>
        data={list}
        totalCount={total}
        columns={cols}
        page={pagination.page}
        pageSize={pagination.pageSize}
        setPage={pagination.setPage}
        setPageSize={pagination.setPageSize}
        sortKey={filters.sortKey}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortKey}
        filters={filterItems}
        onReset={() => {
          filters.setStatus("");
          filters.setSearch("");
        }}
        onApply={() => {}}
      />

      {createModalOpen && (
        <CreateDailyWorkModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onSuccess={handleCreateSuccess}
        />
      )}

      {updateModalOpen && selectedDailyWork && (
        <UpdateDailyWorkModal
          open={updateModalOpen}
          onOpenChange={setUpdateModalOpen}
          dailyWork={selectedDailyWork}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {bulkUploadModalOpen && (
        <BulkUploadModal
          isOpen={bulkUploadModalOpen}
          onClose={() => setBulkUploadModalOpen(false)}
          onUploadComplete={() => {
            refetch();
          }}
        />
      )}
    </MainLayout>
  );
}
